import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { mockData } from '../data/mock';
import { Link } from 'react-router-dom';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const categories = ['All', 'Blockchain', 'AI & Technology', 'Compliance', 'ESG Insights'];

  const filteredPosts = selectedCategory === 'All'
    ? mockData.blog
    : mockData.blog.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" style={{ background: 'linear-gradient(180deg, #A9C9D9 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Insights & Resources
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto">
              Deep dives into blockchain ESG, carbon markets, compliance, and the future of sustainability
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 px-6 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className="px-6 py-3 rounded-full font-semibold transition-all duration-300"
                style={{
                  background: selectedCategory === category ? '#7CB8D6' : 'transparent',
                  color: selectedCategory === category ? '#FFFFFF' : '#1A1A1A',
                  border: `2px solid ${selectedCategory === category ? '#7CB8D6' : '#E5E7EB'}`,
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {filteredPosts.map((post, index) => (
              <BlogCard key={post.id} post={post} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-32 px-6" style={{ background: 'linear-gradient(135deg, #7CB8D6 0%, #A9C9D9 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white">
              Stay Updated
            </h2>
            <p className="text-xl text-white/90 mb-12">
              Get the latest insights on ESG, blockchain, and sustainability delivered to your inbox
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full text-lg focus:outline-none focus:ring-2"
                style={{ borderColor: '#7CB8D6' }}
              />
              <button
                type="submit"
                className="btn-premium px-8 py-4 rounded-full text-lg font-semibold"
                style={{ background: '#F4D740', color: '#1A1A1A' }}
              >
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const BlogCard = ({ post, index }) => {
  return (
    <motion.article
      className="card-premium rounded-3xl bg-white border-2 border-gray-100 overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      {/* Featured Image */}
      <Link to={`/blog/${post.id}`} className="block aspect-video overflow-hidden bg-gradient-to-br from-blue-100 to-yellow-100">
        <img
          src={post.image}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
        />
      </Link>

      {/* Content */}
      <div className="p-8">
        <div className="flex items-center gap-4 mb-4">
          <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: 'rgba(124, 184, 214, 0.1)', color: '#7CB8D6' }}>
            {post.category}
          </span>
          <div className="flex items-center text-sm text-gray-500">
            <Clock size={14} className="mr-1" />
            {post.readTime}
          </div>
        </div>

        <Link to={`/blog/${post.id}`}>
          <h3 className="text-xl font-bold mb-3 line-clamp-2 hover:opacity-70 transition-opacity" style={{ color: '#1A1A1A' }}>
            {post.title}
          </h3>
        </Link>
        <p className="text-gray-600 mb-6 line-clamp-3">
          {post.excerpt}
        </p>

        <div className="flex items-center justify-between pt-6 border-t border-gray-100">
          <div className="flex items-center text-sm text-gray-600">
            <User size={16} className="mr-2" />
            {post.author}
          </div>
          <Link
            to={`/blog/${post.id}`}
            data-testid={`read-more-${post.id}`}
            className="flex items-center font-semibold hover:gap-2 transition-all duration-300"
            style={{ color: '#7CB8D6' }}
          >
            Read More
            <ArrowRight size={18} className="ml-1" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default Blog;